import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Button, ButtonProps, Stack, Typography } from "@mui/material";
import { defaultDataroomFilesFilters } from "constants/datarooms";
import { DataroomFilesFilters, FilterOptions } from "store/types";

import { FilterChips, PluralFiltersKeys } from "./FilterChips";
import { FiltersDrawer } from "./FiltersDrawer";

const buttonStyles: ButtonProps["sx"] = {
  height: "26px",
  fontSize: "15px",
  textTransform: "none",
  fontWeight: "400"
};

interface Props {
  filters: DataroomFilesFilters;
  setFilters: (callback: (filters: DataroomFilesFilters) => DataroomFilesFilters) => void;
  allFilterOptions: { accounts: string[]; docTypes: string[]; deals: string[]; altiusDeals: string[] };
  refetchFiltersOptions: (filters: DataroomFilesFilters) => Promise<FilterOptions>;
  docType?: string;
  accName?: string;
}

export const Filters: React.FC<Props> = ({ filters, setFilters, docType, accName, ...rest }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const isFiltered = useMemo(() => {
    return Object.values(filters)
      .filter((item) => Array.isArray(item))
      .some((item) => item.length);
  }, [filters]);

  const disabledMultiItems = useMemo<Array<PluralFiltersKeys>>(() => {
    const result: PluralFiltersKeys[] = [];
    if (docType) {
      result.push("docTypes");
    }
    if (accName) {
      result.push("accounts");
    }
    return result;
  }, [docType, accName]);

  return (
    <>
      <Stack direction="row" sx={{ alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Button
          sx={{ ...buttonStyles, backgroundColor: isFiltered ? "#dae0e2" : "transparent" }}
          onClick={() => setIsDrawerOpen(true)}
          variant="outlined"
          size="sm"
          startIcon={<FilterAltIcon />}
        >
          Filters
        </Button>
        <FilterChips filters={filters} setFilters={setFilters} disabledMultiItems={disabledMultiItems} />
        <Stack
          sx={{
            color: "#0a3142",
            flexShrink: 0,
            gap: "6px",
            alignItems: "center",
            cursor: "pointer"
          }}
          direction="row"
          onClick={() =>
            setFilters(() => ({
              ...defaultDataroomFilesFilters,
              ...(docType && { docTypes: [docType] }),
              ...(accName && { accounts: [accName] })
            }))
          }
        >
          <CloseIcon sx={{ width: "18px", height: "19px" }} />
          <Typography sx={{ whiteSpace: "nowrap", fontSize: "15px" }}>Clear All Filters</Typography>
        </Stack>
      </Stack>
      <FiltersDrawer
        filters={filters}
        setFilters={setFilters}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        docType={docType}
        accName={accName}
        {...rest}
      />
    </>
  );
};
